import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import type { Tenant } from '@/config/tenants'

/* OIDC auth built on the vetted oidc-client-ts UserManager (browser OIDC/PKCE
   is security-critical - we do not hand-roll it). One manager per tenant, set
   up at bootstrap. The portal redirects to the tenant's Zitadel instance
   (login.<brand>), which shows our email + OTP login UI; on return the code is
   exchanged for tokens here. The webservice validates the resulting JWT. */
let manager: UserManager | null = null

export function initAuth(tenant: Tenant): void {
  if (!tenant.oidc.clientId) {
    // Tenant not yet enabled for OIDC (e.g. PLCC before its instance exists).
    manager = null
    return
  }
  manager = new UserManager({
    authority: tenant.oidc.authority,
    client_id: tenant.oidc.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    // offline_access yields a refresh token; oidc-client-ts then renews via a
    // refresh_token grant (a fetch to the token endpoint), not an iframe. We
    // deliberately configure no silent_redirect_uri, so iframe silent renew is
    // not possible - renewal stays fetch-based and the CSP needs no frame-src.
    // The zitadel metadata scope surfaces the user's `sap_id` metadata as a
    // claim on the access token; the webservice maps the partner to a customer
    // by sap_id (emails are not unique across SAP debitors).
    scope: 'openid profile email offline_access urn:zitadel:iam:user:metadata',
    // Long, sticky sessions: silently renew from the refresh token before the
    // access token expires; persist across tabs/restarts.
    automaticSilentRenew: true,
    // Bound the renewal request so a hung token endpoint fails fast (surfacing
    // an addSilentRenewError) rather than hanging the sticky-session renewal.
    silentRequestTimeoutInSeconds: 10,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // stateStore MUST be localStorage: the login round-trip goes cross-origin to
    // the IdP and back, and the one-click OTP email link can complete in a
    // different tab. sessionStorage (per-tab, dropped on some mobile/incognito
    // cross-origin redirects) loses the PKCE state -> "No matching state found".
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
  })
}

export function authEnabled(): boolean {
  return manager !== null
}

export function getAuth(): UserManager {
  if (!manager) {
    throw new Error('OIDC is not configured for this tenant')
  }
  return manager
}

/* Test seam */
export function setAuthManagerForTesting(m: UserManager | null): void {
  manager = m
}

export type { User }
